import React, { createContext, useContext, useState, useCallback } from "react";
import { logger } from "../utils/logger";

// Libraries to load when Maps is needed
export const GOOGLE_MAPS_LIBRARIES: (
  | "places"
  | "drawing"
  | "geometry"
  | "visualization"
)[] = ["places", "drawing", "geometry"];

interface GoogleMapsContextValue {
  isLoaded: boolean;
  loadError: Error | null;
  loadMapsApi: () => Promise<void>;
}

const GoogleMapsContext = createContext<GoogleMapsContextValue | null>(null);

interface GoogleMapsProviderProps {
  children: React.ReactNode;
}

// Track loading state globally to prevent multiple loads
let _isLoading = false;
let isLoadedGlobal = false;
let loadErrorGlobal: Error | null = null;
let loadPromise: Promise<void> | null = null;

/**
 * Lazy-loading Google Maps Provider
 *
 * Google Maps API is NOT loaded until a component explicitly calls loadMapsApi()
 * This prevents loading ~150KB of Google Maps JS on pages that don't need it
 */
export const GoogleMapsProvider: React.FC<GoogleMapsProviderProps> = ({
  children,
}) => {
  const [isLoaded, setIsLoaded] = useState(isLoadedGlobal);
  const [loadError, setLoadError] = useState<Error | null>(loadErrorGlobal);
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  const loadMapsApi = useCallback(async () => {
    // Already loaded
    if (isLoadedGlobal) {
      setIsLoaded(true);
      return;
    }

    // Already loading - wait for it
    if (loadPromise) {
      await loadPromise;
      setIsLoaded(isLoadedGlobal);
      setLoadError(loadErrorGlobal);
      return;
    }

    // No API key
    if (!apiKey) {
      const error = new Error(
        "[GoogleMapsProvider] Google Maps API key is not configured",
      );
      console.error(error.message);
      setLoadError(error);
      loadErrorGlobal = error;
      return;
    }

    // Start loading
    _isLoading = true;
    logger.debug("[GoogleMapsProvider] Loading Google Maps API lazily...");

    loadPromise = new Promise<void>((resolve, reject) => {
      // Check if already loaded by another means
      if (typeof google !== "undefined" && google.maps) {
        isLoadedGlobal = true;
        setIsLoaded(true);
        logger.debug("[GoogleMapsProvider] Google Maps already available");
        resolve();
        return;
      }

      // Create script element
      const script = document.createElement("script");
      const libraries = GOOGLE_MAPS_LIBRARIES.join(",");
      script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=${libraries}`;
      script.async = true;
      script.defer = true;

      script.onload = () => {
        isLoadedGlobal = true;
        _isLoading = false;
        setIsLoaded(true);
        logger.debug("[GoogleMapsProvider] Google Maps loaded successfully");
        resolve();
      };

      script.onerror = () => {
        const error = new Error(
          "[GoogleMapsProvider] Failed to load Google Maps script",
        );
        loadErrorGlobal = error;
        _isLoading = false;
        setLoadError(error);
        console.error(error.message);
        reject(error);
      };

      document.head.appendChild(script);
    });

    try {
      await loadPromise;
    } catch (_e) {
      // Error already handled
    }
  }, [apiKey]);

  return (
    <GoogleMapsContext.Provider value={{ isLoaded, loadError, loadMapsApi }}>
      {children}
    </GoogleMapsContext.Provider>
  );
};

/**
 * Hook to access Google Maps loading state and trigger loading
 */
export const useGoogleMapsLoader = () => {
  const context = useContext(GoogleMapsContext);
  if (!context) {
    throw new Error(
      "useGoogleMapsLoader must be used within a GoogleMapsProvider",
    );
  }
  return context;
};

export default GoogleMapsProvider;
